import { DisplayCardTypes } from 'components/DisplayCard'
import React, { useEffect, useReducer, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import api from '../../api/api'

import {
  Button,
  DisplayCard,
  InnerSpinner,
  Page,
  PayoutIBANFormFields,
  PayoutTariffFormFields,
  Badge,
  Checkbox,
} from '../../components'

import { BadgeState } from '../../components/Badge'
import { PageTheme } from '../../typeScript/types/PageTheme'
import { showApiError } from '../../utils/error-handler'
import {
  getValidationRules,
  ruleNames,
  validationService,
  hasValidationError,
  validationErrorTranslation,
} from '../../validation'
import moment from 'moment'
import { ButtonType } from 'typeScript/types/ButtonTypes'
import styles from './Payout.module.scss'
import { removeWhitespace } from 'utils/strings'

import tick from '../../assets/GreenTick.svg'
import StatusKYC from 'components/KYC/StatusKYC'
import { Modal, Spacer, SpacerSizeTypes, useStore } from '@envision/ev-ui-component-library'
import OnboardingKYC from 'components/KYC/OnboardingKYC'
import { useNavigate } from 'react-router-dom'
import ExternalCardProvider from 'constants/externalCardProvider'
import { IRootState } from 'store'
import { ILoggedUser } from 'typeScript/interfaces/IUser'

const termsLink = 'https://www.univers.de/de-DE/service/agb'

enum Status {
  PENDING = 'PENDING',
  PLANNED = 'PLANNED',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  REJECTED = 'REJECTED',
}

enum Locale {
  GERMAN = 'de-DE',
  ENGLISH = 'en-US',
}
interface IPayoutHistory {
  energyPrice?: number
  priceUnit?: string
  contractFileType?: string
  createdDate?: string
  lastModifiedDate?: string
  startDate?: string
  registrationStatus?: string
}

interface IIBANInfo {
  lastModifiedDate: string
}

const locale = Locale.GERMAN

function Payout() {
  const dispatch = useDispatch()

  const [eventEmitter] = useStore.eventEmitter()

  const { t } = useTranslation()
  const [validationErrors, setValidationErrors] = useState<object[]>([])

  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false)
  const [history, setHistory] = useState<IPayoutHistory[]>([])
  const [IBANInfo, setIBANInfo] = useState<IIBANInfo>()
  const [isIBANDataValid, setIsIBANDataValid] = useState(true)
  const [isInitialFormVisible, setIsInitialFormVisible] = useState(false)
  const [isTariffFormVisible, setIsTariffFormVisible] = useState(false)
  const [isIBANFormVisible, setIsIBANFormVisible] = useState(false)
  const [isButtonClickable, setIsButtonClickable] = useState(true)

  const user = useSelector<IRootState, ILoggedUser | null>(
    (state) => state.user.user
  )

  const canAddExternalCard =
    user?.externalCardUse === ExternalCardProvider.SHELL

  const [hasNoRfidCards, setHasNoRfidCards] = useState(false)
  const navigate = useNavigate()

  const fieldValuesIBANObject = {
    firstName: '',
    lastName: '',
    iban: '',
  }
  const fieldValuesTariffObject = {
    energyPrice: '',
    startDate: '',
    energyContract: '',
  }

  const [fieldValues, setFieldValues] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      ...fieldValuesTariffObject,
      ...fieldValuesIBANObject,
    }
  )
  const [ibanFieldValues, setIbanFieldValues] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    fieldValuesIBANObject
  )
  const [tariffFieldValues, setTariffFieldValues] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    fieldValuesTariffObject
  )

  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] =
    useState(false)

  const validationRulesTariff = [
    ruleNames.payout.energyPrice,
    ruleNames.payout.startDate,
    ruleNames.payout.energyContract,
  ]
  const validationRulesIban = [
    ruleNames.payout.firstName,
    ruleNames.payout.lastName,
    ruleNames.payout.iban,
  ]
  const validationRulesTermsAndConditionsAccepted = [
    ruleNames.payout.termsAndConditionsAccepted,
  ]

  useEffect(() => {
    getPayoutHistory()
    getPayoutIBANInfo()
    fetchRfidCards()
  }, [])

  const fetchRfidCards = () => {
    api.assets
      .getRFIDAssets()
      .then((response) => {
        if (response?.data && Array.isArray(response.data)) {
          // Check if user has any RFID cards with DRIIVZ integration
          const hasCards = response.data.some((card) =>
            card.integrations?.some((i) => i.cpo === 'DRIIVZ')
          )
          setHasNoRfidCards(!hasCards)
        } else {
          setHasNoRfidCards(true)
        }
      })
      .catch(() => {
        setHasNoRfidCards(true)
      })
  }

  const formatterDecimal = (price) =>
    new Intl.NumberFormat(locale, {
      style: 'decimal',
      maximumFractionDigits: 8,
    }).format(price)

  const formatToLocaleCent = (price) => {
    if (price && !isNaN(price)) {
      return formatterDecimal(price)
    }
  }

  const validationRules = (params, validationRules) => {
    const rules = getValidationRules(validationRules)
    const validation = validationService(params, rules)
    return validation
  }

  /**
   * Convert the german comma to dot
   * @param params
   */
  const formatEnergyPriceInParams = (params) => {
    if (params.energyPrice && typeof params.energyPrice === 'string') {
      locale === Locale.GERMAN &&
        (params.energyPrice = parseFloat(params.energyPrice.replace(/,/g, '.')))
    }
  }

  const getPayoutHistory = () => {
    api.payout
      .getPayoutHistory()
      .then((resp) => {
        const tariffs = resp.data.energyTariffs

        if (tariffs && tariffs.length > 0) {
          // Sorting by startDate
          tariffs.sort((a, b) => a.startDate + b.startDate)

          setHistory(tariffs)
          setIsInitialFormVisible(false)
          setIsTariffFormVisible(false)
          setIsIBANFormVisible(false)
        } else {
          setIsInitialFormVisible(true)
        }
      })
      .catch(() => {})
      .then(() => {
        setIsHistoryLoaded(true)
      })
  }

  const getPayoutIBANInfo = () => {
    api.payout
      .getPayoutIBANInfo()
      .then((resp) => {
        setIBANInfo(resp.data.ibanRegistration)
      })
      .catch(() => {})
      .then(() => {})

    setIsInitialFormVisible(false)
    setIsTariffFormVisible(false)
    setIsIBANFormVisible(false)
  }

  const handleInitialSubmit = (event) => {
    if (event) {
      event.preventDefault()
    }

    if (!isButtonClickable) return

    const params = { ...fieldValues, termsAndConditionsAccepted }
    const validation = validationRules(
      params,
      validationRulesTariff
        .concat(validationRulesIban)
        .concat(validationRulesTermsAndConditionsAccepted)
    )

    if (validation.passes()) {
      setValidationErrors([])
      setIsButtonClickable(false)
      setIsIBANDataValid(true)

      formatEnergyPriceInParams(params)

      doIBANRequest(params, doTariffRequest)
    } else {
      const errors = validation.getErrors()
      setValidationErrors(errors)
    }
  }

  const handleTariffSubmit = (event) => {
    if (event) {
      event.preventDefault()
    }

    if (!isButtonClickable) return

    const params = { ...tariffFieldValues, termsAndConditionsAccepted }
    const validation = validationRules(
      params,
      validationRulesTariff.concat(validationRulesTermsAndConditionsAccepted)
    )

    if (validation.passes()) {
      setValidationErrors([])
      setIsButtonClickable(false)

      formatEnergyPriceInParams(params)
      doTariffRequest(params)
    } else {
      const errors = validation.getErrors()
      setValidationErrors(errors)
    }
  }

  const handleIBANSubmit = (event) => {
    if (event) {
      event.preventDefault()
    }

    if (!isButtonClickable) return

    const params = { ...ibanFieldValues, termsAndConditionsAccepted }
    const validation = validationRules(
      params,
      validationRulesIban.concat(validationRulesTermsAndConditionsAccepted)
    )

    if (validation.passes()) {
      setValidationErrors([])
      setIsButtonClickable(false)
      setIsIBANDataValid(true)

      doIBANRequest(params, null)
    } else {
      const errors = validation.getErrors()
      setValidationErrors(errors)
    }
  }

  // const isIbanValid = (iban: string) => {
  //     fetch(`https://openiban.com/validate/${iban}`)
  //         .then((results) => results.json())
  //         .then((data) => {
  //             console.warn(999, data);
  //         });
  // };

  const doIBANRequest = (params, successCallback) => {
    const IBANParams = {
      firstName: params.firstName,
      lastName: params.lastName,
      iban: removeWhitespace(params.iban),
      termsAndConditionsAccepted: params.termsAndConditionsAccepted,
    }

    // isIbanValid(params.iban);

    api.payout
      .payoutIBANRegistration(IBANParams)
      .then(() => {
        setIbanFieldValues(fieldValuesIBANObject)
        !successCallback && setTermsAndConditionsAccepted(false)
        successCallback ? successCallback(params) : getPayoutIBANInfo()
      })
      .catch((error) => {
        setIsIBANDataValid(false)
        showApiError(error.response, dispatch, t)
      })
      .finally(() => {
        setIsInitialFormVisible(false)
        setIsButtonClickable(true)
        eventEmitter.emit('fetchKYCStatus', { state: 'true' })
      })
  }

  const doTariffRequest = (params) => {
    const energyTariffParams = {
      energyPrice: params.energyPrice,
      startDate: params.startDate,
      termsAndConditionsAccepted: params.termsAndConditionsAccepted,
    }

    api.payout
      .payoutEnergyTariffRegistration(energyTariffParams, params.energyContract)
      .then(() => {
        setTariffFieldValues(fieldValuesTariffObject)
        setTermsAndConditionsAccepted(false)
        getPayoutHistory()
      })
      .catch((error) => {
        console.warn(error.response)
        showApiError(error.response, dispatch, t)
      })
      .finally(() => {
        setIsButtonClickable(true)
      })
  }

  const renderedSubmitButton = (callback) => (
    <div className="buttons">
      {history.length > 0 && (
        <Button
          type={ButtonType.secondary}
          onClick={() => {
            callback()
            window.scrollTo(0, 0)
            setTermsAndConditionsAccepted(false)
          }}
        >
          {t('payout:button_hide_form')}
        </Button>
      )}
      <Button buttonType="submit" disabled={!isButtonClickable}>
        {t('common:buttons.submit')}
      </Button>
    </div>
  )
  const renderedFormToggleButton = () => (
    <div className={`buttons`}>
      <Button
        buttonType="button"
        onClick={() => {
          setIsTariffFormVisible(!isTariffFormVisible)
          window.scrollTo(0, 0)
        }}
      >
        {isTariffFormVisible
          ? t('payout:button_hide_form')
          : t('payout:button_show_tariff_form')}
      </Button>
    </div>
  )

  const renderedCheckbox = () => (
    <Checkbox
      id="termsAndConditionsAccepted"
      className="mb__xs"
      checked={termsAndConditionsAccepted}
      hasValidationError={hasValidationError(
        ruleNames.payout.termsAndConditionsAccepted,
        validationErrors
      )}
      validationErrorLabel={validationErrorTranslation(
        ruleNames.payout.termsAndConditionsAccepted,
        validationErrors,
        t
      )}
      onChange={() => {
        setTermsAndConditionsAccepted(!termsAndConditionsAccepted)
      }}
      label={
        <span
          dangerouslySetInnerHTML={{
            __html: t('checkout:personal_info_terms', { termsLink: termsLink }),
          }}
        />
      }
    />
  )

  return (
    <Page
      pageTheme={PageTheme.light}
      title={t('payout:header.title')}
      intro={
        history.length === 0 ? t('payout:no_history') : t('payout:with_history')
      }
    >
      <div className={`2xl:w-9/12 ${styles.payout}`}>
      {/* Show notice when: no RFID cards, can add external cards, and has tariff */}
      {hasNoRfidCards && canAddExternalCard && history.length > 0 && (
        <div className="mb-10 rounded-lg border border-amber-400 bg-amber-50 py-5">
          <p
            dangerouslySetInnerHTML={{
              __html: t('payout:external_card_notice'),
            }}
            className="text-amber-800 [&_a]:underline [&_a]:font-semibold [&_a]:text-amber-900 [&_a]:hover:text-amber-700"
            onClick={(e) => {
              const target = e.target as HTMLElement
              if (target.tagName === 'A') {
                const href = target.getAttribute('href')
                if (href && href.startsWith('/')) {
                  e.preventDefault()
                  navigate(href)
                }
              }
            }}
          />
        </div>
      )}
        <InnerSpinner isActive={!isHistoryLoaded}>
          {!(
            isInitialFormVisible ||
            isTariffFormVisible ||
            isIBANFormVisible
          ) && (
            <>
              <DisplayCard type={DisplayCardTypes.PLAIN} className="xl:w-8/12">
                <h3 className="mb-5 text-fg_primary">
                  {t('payout:iban_header')}
                </h3>

                <div className="flex justify-between">
                  <div>
                    {t('payout:iban_added')}
                    {'  '}
                    <img
                      width="25px"
                      style={{ verticalAlign: 'middle' }}
                      src={tick}
                      alt=""
                    />
                  </div>

                  <span
                    className={styles.link}
                    onClick={() => setIsIBANFormVisible(!isIBANFormVisible)}
                  >
                    {isIBANFormVisible
                      ? t('payout:button_hide_form')
                      : t('payout:button_show_IBAN_form')}
                  </span>
                </div>
                <br />

                <p className={styles.date}>
                  {t('payout:iban_creation_date')}{' '}
                  {moment(IBANInfo?.lastModifiedDate)
                    .locale('de')
                    .format('DD.MM.YYYY HH:mm')}
                </p>

                <hr className={`-mx-5 xl:-mx-8 my-5  ${styles.divider}`} />

                <StatusKYC />
              </DisplayCard>

              <div className="h-5"> </div>

              <DisplayCard type={DisplayCardTypes.PLAIN} className="xl:w-8/12">
                <h3 className="mb-5 text-fg_primary">
                  {t('payout:tariff_header')}
                </h3>
                <p className="mb-8 text-fg_primary">
                  {t('payout:tariff_intro')}
                </p>

                {history.map((historyItem, idx) => (
                  <div
                    className={`${styles.itemBox}`}
                    key={idx.toLocaleString()}
                  >
                    <div className="flex justify-between mb-2">
                      <div className={styles.itemName}>
                        {t('payout:history_header_price')}
                      </div>
                      <div>
                        {formatToLocaleCent(historyItem.energyPrice)}{' '}
                        {historyItem.priceUnit}
                      </div>
                    </div>
                    <div className="flex justify-between mb-2">
                      <div className={styles.itemName}>
                        {t('payout:history_header_start_date')}
                      </div>
                      <div>
                        {moment(historyItem.startDate).format('DD.MM.YYYY')}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <div className={styles.itemName}>
                        {t('payout:history_header_status')}
                      </div>

                      <Badge
                        state={
                          historyItem.registrationStatus === Status.ACTIVE
                            ? BadgeState.SUCCESS
                            : historyItem.registrationStatus === Status.PLANNED
                              ? BadgeState.INFO
                              : historyItem.registrationStatus ===
                                  Status.REJECTED
                                ? BadgeState.ERROR
                                : BadgeState.DEFAULT
                        }
                      >
                        <p className="text-md">
                          {historyItem.registrationStatus === Status.ACTIVE
                            ? t('payout:history_status_succeeded')
                            : historyItem.registrationStatus === Status.PLANNED
                              ? t('payout:history_status_ongoing')
                              : historyItem.registrationStatus ===
                                  Status.REJECTED
                                ? t('payout:history_status_failed')
                                : historyItem.registrationStatus ===
                                    Status.PENDING
                                  ? t('payout:history_status_pending')
                                  : t('payout:history_status_expired')}
                        </p>
                      </Badge>
                    </div>
                  </div>
                ))}

                {history.length > 0 && renderedFormToggleButton()}
              </DisplayCard>
            </>
          )}
        </InnerSpinner>

        {/* INITIAL FORM */}
        {isInitialFormVisible && (
          <DisplayCard
            type={DisplayCardTypes.PLAIN}
            style={{ backgroundColor: 'var(--surface-300)' }}
          >
            <form onSubmit={handleInitialSubmit}>
              <DisplayCard
                style={{ paddingTop: '2rem', paddingBottom: '0px' }}
                type={DisplayCardTypes.PLAIN}
                label={t('payout:form_title_energy_tariff')}
                intro={t('payout:tariff_intro_text')}
              >
                <PayoutTariffFormFields
                  validationErrors={validationErrors}
                  setFieldValues={setFieldValues}
                  fieldValues={fieldValues}
                  isUpdateForm={history.length > 0}
                  isInitialForm={true}
                />
              </DisplayCard>
              <div className="h-5"> </div>
              <DisplayCard
                type={DisplayCardTypes.PLAIN}
                intro={t('payout:kyc_explanation_text')}
                label={t('payout:form_title')}
              >
                <PayoutIBANFormFields
                  validationErrors={validationErrors}
                  setFieldValues={setFieldValues}
                  isIBANDataValid={isIBANDataValid}
                />
              </DisplayCard>
              <div className="pt-8 px-8">
                {renderedCheckbox()}
                {renderedSubmitButton(() =>
                  setIsInitialFormVisible(!isInitialFormVisible)
                )}
              </div>
            </form>
          </DisplayCard>
        )}

        {/* TARIFF FORM */}
        {isTariffFormVisible && (
          <DisplayCard
            type={DisplayCardTypes.PLAIN}
            label={t('payout:tariff_header')}
            intro={t('payout:form_intro')}
          >
            <form onSubmit={handleTariffSubmit}>
              <>
                <PayoutTariffFormFields
                  validationErrors={validationErrors}
                  setFieldValues={setTariffFieldValues}
                  fieldValues={tariffFieldValues}
                  isUpdateForm={history.length > 0}
                />
                {renderedCheckbox()}
                {renderedSubmitButton(() =>
                  setIsTariffFormVisible(!isTariffFormVisible)
                )}
              </>
            </form>
          </DisplayCard>
        )}

        {/* IBAN FORM */}
        {isIBANFormVisible && (
          <DisplayCard
            type={DisplayCardTypes.PLAIN}
            label={t('payout:form_title')}
            intro={t('payout:form_intro')}
          >
            <form onSubmit={handleIBANSubmit}>
              <>
                <PayoutIBANFormFields
                  validationErrors={validationErrors}
                  setFieldValues={setIbanFieldValues}
                  isIBANDataValid={isIBANDataValid}
                />
                {renderedCheckbox()}
                {renderedSubmitButton(() =>
                  setIsIBANFormVisible(!isIBANFormVisible)
                )}
              </>
            </form>
          </DisplayCard>
        )}
      </div>
      <Modal
        id="kyc-modal"
        onClose={() => {
          eventEmitter.emit('fetchKYCStatus', { state: 'true' })
        }}
      >
        <OnboardingKYC />
      </Modal>
    </Page>
  )
}

export default Payout
